/**
 * AppOverlayShell
 *
 * The blurred, full-viewport scrim shared by the two app-level blocking
 * overlays (`BudgetExceededOverlay` / `AccountSuspendedOverlay`).
 *
 * Flutter source of truth (`overlay_widget.dart:14-191`): both overlays are a
 * `SizedBox` filling the viewport with a `Stack` of
 *   1. `BackdropFilter(blur sigmaX/Y = DimensionConstants.overlayBlur{X,Y})`
 *      over a light background at 50 % alpha (budget, `:24-36`) / 30 % alpha
 *      (suspended, `:122-133`), and
 *   2. a centred `Padding` + `Column` carrying the copy and the controls.
 *
 * Web deviations (allowed by `presentation.md` → "Web vs Flutter UI parity
 * scope" — styling idiom only, no behaviour change):
 *  - CSS `backdrop-filter: blur(...)` instead of Flutter's `BackdropFilter`;
 *    sigma 10 ≈ a 10 px blur radius (`responsive.appOverlay.blur`).
 *  - The alpha-scrim colors come from the project's overlay tokens
 *    (`theme.modalOverlay` = 50 %, `theme.loadingOverlay` = 30 %) rather than
 *    a one-off alpha-composited literal.
 *  - The content column is capped at `responsive.appOverlay.contentMaxWidth`
 *    and vertically centred, so the copy stays readable on a wide desktop
 *    viewport instead of stretching edge to edge.
 *
 * The shell deliberately has **no** click-outside / Escape dismiss handler:
 * each overlay owns its own exits (see the two call sites), and the suspended
 * one is terminal by design.
 */

import React from 'react';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { APP_OVERLAY_CONSTANTS } from '@/presentation/constants/appOverlayConstants';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface AppOverlayShellProps {
  /** `data-qa-id` spread onto the overlay root so QA can assert presence. */
  qaId: string;
  /**
   * Scrim color behind the blur. Callers pass a theme overlay token — the
   * Flutter alpha differs per overlay (50 % budget / 30 % suspended).
   */
  scrimColor?: string;
  /** Accessible name of the blocking surface (the overlay's own title). */
  ariaLabel: string;
  children: React.ReactNode;
}

export const AppOverlayShell: React.FC<AppOverlayShellProps> = ({
  qaId,
  scrimColor,
  ariaLabel,
  children,
}) => {
  const responsive = useResponsiveDesign();
  const blurPx = responsive.appOverlay.blur;

  return (
    <div
      {...qaAttr({ id: qaId })}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      style={{
        position: 'fixed',
        inset: 0,
        // Above every other app surface — the app is unusable while either
        // overlay is up (Flutter inserts these into the root `Overlay`).
        zIndex: APP_OVERLAY_CONSTANTS.zIndex,
        backgroundColor: scrimColor,
        backdropFilter: `blur(${blurPx}px)`,
        WebkitBackdropFilter: `blur(${blurPx}px)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Flutter wraps the column in `EdgeInsets.all(32.0)` (`:35`).
        padding: responsive.spacing['3xl'],
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: responsive.appOverlay.contentMaxWidth,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: responsive.gap['2xl'],
          textAlign: 'center',
        }}
      >
        {children}
      </div>
    </div>
  );
};
